<template lang="pug">
include ../pug/svg
section#settings
	Form(ref="settingsForm" :validation-schema="settingsSchema" v-slot="{ values, errors, meta }")
		//-pre values: {{values}}
		//-pre errors: {{meta}}
		//-pre meta: {{values}}
		p(v-if="!verifiedUser" style="margin-top:30px;")
			span(v-if="emailUser") Please provide a new password to complete your account setup&hellip;
			span(v-else) Please provide a new password and recovery credentials to complete your account setup&hellip;
		FieldText(name="emailUser" type="hidden" v-model="emailUser")
		FieldText(name="verifiedUser" type="hidden" v-model="verifiedUser")
		FieldText(name="recoverySet" type="hidden" v-model="recoverySet")
		FieldText(name="securityQuestion1Mounted" type="hidden" v-model="securityQuestion1Mounted")
		FieldText(name="securityQuestion2Mounted" type="hidden" v-model="securityQuestion2Mounted")
		div.wrapper
			div.fieldset.c2
				h4 {{terminology('interface', 'signin', 'singular')}} Credentials
				p.desc Thinkhub {{terminology('interface', 'signin', 'singular')}} credentials.
				
				FieldText(name="email" label="Email or Username" v-model="email" :required="true" :errors="errors" @keyup="enableCheck")
				
				//-FieldText(name="password" type="password" label="Password" :placeholder="passwordPlaceholder" v-model="password" :required="true" :validateOnInput="true" :errors="errors" @keyup="passwordChange" @blur="passwordChange")
				FieldText(name="password" type="password" label="Password" :placeholder="passwordPlaceholder" v-model="password" :required="true" :validateOnInput="true" :errors="errors" @keyup="passwordChange")
		
				//-FieldText(name="passwordRepeat" type="password" label="Repeat Password" :placeholder="passwordRepeatPlaceholder" v-model="passwordRepeat" :required="true" :errors="errors" @keyup="enableCheck" @blur="enableCheck" :disabled="disablePasswordRepeat")
				
			div.fieldset.c2(v-if="!emailUser")
				h4 Password Recovery
				p.desc Account verification checks for forgotten password.
				
				FieldSelect(name="securityQuestion1" label="Security Question One" v-model="securityQuestion1" :options="filteredSecurityQuestionsOne" :errors="errors" @change="securityQuestionChange(1)" :required="true")
				
				FieldSelect(name="securityQuestion2" label="Security Question Two" v-model="securityQuestion2" :options="filteredSecurityQuestionsTwo" :errors="errors" @change="securityQuestionChange(2)" :required="true")

				FieldText(name="securityAnswer1" label="Answer To Security Question One" :placeholder="securityAnswerOnePlaceholder" v-model="securityAnswer1" :required="true" :errors="errors" @keyup="enableCheck")
				
				FieldText(name="securityAnswer2" label="Answer To Security Question Two" :placeholder="securityAnswerTwoPlaceholder" v-model="securityAnswer2" :required="true" :errors="errors" @keyup="enableCheck")
				
				FieldText(name="securityMemorable" label="Memorable Word" :placeholder="securityMemorablePlaceholder" v-model="securityMemorable" :required="true" :errors="errors" @keyup="enableCheck")
			
			div.fieldset.c4.grow(v-else)
				h4 Notification Summary
				
				p.desc The next notification check will be performed at 
					span {{nextCheck}}
					
				FieldBoolean(v-model="useNotification" label="Email Notifiactions" yesLabel="Active" noLabel="Inactive" @fieldUpdated="enableCheck")
				
				FieldSelect(name="frequency" label="Frequency" v-model="notificationFrequency" :placeholder="false" :options="frequencies" :errors="errors" @change="enableCheck" :required="true" :disabled="!useNotification")
				
				FieldSelect(v-if="notificationFrequency==='weekly'" name="notificationDay" label="Day" v-model="notificationDay" :placeholder="false" :options="days" :errors="errors" @change="enableCheck" :required="true" :disabled="!useNotification")
				
				FieldSelect(v-if="notificationFrequency!=='hourly'" name="notificationTime" label="Time" v-model="notificationTime" :placeholder="false" :options="times" :errors="errors" @change="enableCheck" :required="true" :disabled="!useNotification")
	span.req Required
</template>

<script>
import { Form } from 'vee-validate';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import FieldText from '../components/FieldText';
import FieldSelect from '../components/FieldSelect';
import FieldBoolean from '../components/FieldBoolean';
import { createHelpers } from 'vuex-map-fields';

YupPassword(Yup); // extend yup

const { mapFields: mapUserFields } = createHelpers({
	getterType: 'getUserField',
	mutationType: 'updateUserField',
});

export default {
	name: 'ManageAccountSettings',
	props: ['cancelled','saved'],
	emits: ['storeUpdated'],
	components: {
		Form,
		FieldText,
		FieldSelect,
		FieldBoolean,
	},
	data() {
		const req = 'Required';
		const inv = 'Invalid';
		
		const settingsSchema = Yup.object().shape({
			email: Yup.string().test('emailOrUsername', inv, (value) => {
				if (value) {
					return value.includes('@') ? Yup.string().email().isValidSync(value) : Yup.string().matches(/^[\w-]+$/).isValidSync(value); 
				}
				
				return false;
			}),
			password: Yup.string().when('verifiedUser', {
				is: (verifiedUser) => !verifiedUser,
				//then: Yup.string().label('Password').password().required(req).nullable(),
				//otherwise: Yup.string().label('Password').password().nullable(),
				then: Yup.string().label('Password')
					.min(8, 'Must be at least 8 characters long')
					.minLowercase(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.minUppercase(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.minNumbers(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.minSymbols(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.required(req).nullable(),
				otherwise: Yup.string().label('Password')
					.nullable()
					.transform((curr, orig) => (orig === "" ? null : curr)) // converts '' to null
					.min(8, 'Must be at least 8 characters long')
					.minLowercase(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.minUppercase(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.minNumbers(1, 'Must contain uppercase, lowercase, numeric and symbol characters')
					.minSymbols(1, 'Must contain uppercase, lowercase, numeric and symbol characters'),
			}),
			securityQuestion1: Yup.string().when('emailUser', {
				is: (emailUser) => !emailUser,
				then: Yup.string().required(req).nullable(),
			}),
			securityQuestion2: Yup.string().when('emailUser', {
				is: (emailUser) => !emailUser,
				then: Yup.string().required(req).nullable(),
			}),
			securityAnswer1: Yup.string().when(['emailUser','securityQuestion1','securityQuestion1Mounted'], {
				is: (emailUser, securityQuestion1Mounted, securityQuestion1) => !emailUser && (securityQuestion1 !== securityQuestion1Mounted),
				then: Yup.string().required(req).nullable(),
				otherwise: Yup.string().nullable(),
			}),
			securityAnswer2: Yup.string().when(['emailUser','securityQuestion2','securityQuestion2Mounted'], {
				is: (emailUser, securityQuestion1Mounted, securityQuestion1) => !emailUser && (securityQuestion1 !== securityQuestion1Mounted),
				then: Yup.string().required(req).nullable(),
				otherwise: Yup.string().nullable(),
			}),
			securityMemorable: Yup.string().when(['emailUser','recoverySet'], {
				is: (emailUser, recoverySet) => !emailUser && !recoverySet,
				then: Yup.string().required(req).nullable(),
				otherwise: Yup.string().nullable(),
			}),
		});
		
		return {
			settingsSchema,
			frequencies: [
				{
					option: 'Hourly Notification',
					value: 'hourly',
				},
				{
					option: 'Daily Notification',
					value: 'daily',
				},
				{
					option: 'Weekly Notification',
					value: 'weekly',
				},
			],
			times: [
				{
					option: '9am',
					value: '09:00',
				},
				{
					option: '10am',
					value: '10:00',
				},
			],
			days: [
				{
					option: 'Sunday',
					value: 0,
				},
				{
					option: 'Monday',
					value: 1,
				},
				{
					option: 'Tuesday',
					value: 2,
				},
				{
					option: 'Wednesday',
					value: 3,
				},
				{
					option: 'Thursday',
					value: 4,
				},
				{
					option: 'Friday',
					value: 5,
				},
				{
					option: 'Saturday',
					value: 6,
				},
			],
			notificationDefault: {
				method: 'email',
				frequency: 'weekly',
				time: '09:00',
				day: 1,
			},
			nowInterval: null,
			now: new Date().getTime(),
			securityQuestions: [
				'In what city or town did your parents meet?',
				'To what country did you first fly on a plane?',
				'What was the first concert you attended?',
				'What was the nickname of your favorite childhood friend?',
				'What was the name of your first stuffed toy?',
				'What was your dream job as a child?',
				'What was your favorite subject in school?',
				'What is the name of your favorite cartoon character?',
				'Where were you when you first heard about 9/11?',
				"What is your oldest cousin's first name?",
			],
			recoverySet: 0,
			//passwordRepeat: null,
			securityQuestion1Mounted: null,
			securityQuestion2Mounted: null,
		}
	},
	mounted() {
		this.$store.dispatch('gui/setHeader', {
//			title: 'Account Settings ('+this.email+')',
			title: 'Account Settings',
			backRoute: {
				text: 'Dashboard',
				name: 'Dashboard',
			},
		});
		
		this.nowInterval = setInterval(() => { // ticker
			this.now = new Date().getTime();
		}, 1000);
		
		this.storeDefaults();
	},
	onUnmounted() {
		clearInterval(this.nowInterval);
		this.nowInterval = null;
	},
	computed: {
		...mapUserFields('users', [ // uses custom getter: getUserField
			'email',
			'password',
			'verified',
			'notification',			
			'notification.frequency',			
			'notification.day',			
			'notification.time',
			'securityQuestion1',
			'securityQuestion2',
			'securityAnswer1',
			'securityAnswer2',
			'securityMemorable',
		]),
		useNotification: {
			get() {
				return !this.notification ? false : true;
			},
			set(bool) {
				this.notification = (!bool) ? null : JSON.parse(JSON.stringify(this.notificationDefault)); // reset without reactivity
			},
		},
		filteredSecurityQuestionsOne() {
			return this.securityQuestions.filter(q => q !== this.securityQuestion2);
		},
		filteredSecurityQuestionsTwo() {
			return this.securityQuestions.filter(q => q !== this.securityQuestion1);
		},
		passwordPlaceholder() {
			return (this.verified) ? '••••••' : 'Unsupplied';			
		},
		securityAnswerOnePlaceholder() {
			return (this.recoverySet && this.securityAnswer1 === null) ? '••••••' : 'Unsupplied';
		},
		securityAnswerTwoPlaceholder() {
			return (this.recoverySet && this.securityAnswer2 === null) ? '••••••' : 'Unsupplied';
		},
		securityMemorablePlaceholder() {
			return (this.recoverySet) ? '••••••' : 'Unsupplied';
		},
		notificationFrequency: {
			get() {
				return !this.notification ? this.notificationDefault.frequency : this.notification.frequency;
			},
			set(value) {
				this.frequency = value;
				
				if (value === 'weekly') {
					this.day = this.notificationDefault.day;
					
					if (!this.time) {
						this.time = this.notificationDefault.time;
					}
					
				} else if (value === 'daily') {
					this.day = null;
					
					if (!this.time) {
						this.time = this.notificationDefault.time;
					}
					
				} else if (value === 'hourly') {
					this.time = null;
					this.day = null;
				}
			},
		},
		notificationDay: {
			get() {
				return !this.notification ? this.notificationDefault.day : this.notification.day;
			},
			set(value) {
				this.day = value;
			},
		},
		notificationTime: {
			get() {
				return !this.notification ? this.notificationDefault.time : this.notification.time;
			},
			set(value) {
				this.time = value;
			},
		},
		nextCheck() {
			const ms = 1000 * 60 * 15; // convert 15 minutes to ms
			return new Date(Math.ceil(this.now / ms) * ms).toLocaleTimeString('en-GB', { hour: 'numeric', minute: 'numeric', hour12: true });
		},
		emailUser() {
			return Yup.string().email().isValidSync(this.email) ? 1 : 0;
		},
		verifiedUser() {
			return this.verified ? 1 : 0;
		},
	},
	methods: {
		terminology(groupKey, termKey, varKey) {
			return this.$store.getters['gui/customTerm'](groupKey, termKey, varKey);
		},
		passwordChange() {
			this.enableCheck();
		},
		securityQuestionChange(n) {
			// reset corresponding answer field
			this.$refs.settingsForm.resetField('securityAnswer'+n, {
				value: '', // important as check done against null
			});
			
			this.enableCheck();
		},
		async enableCheck() {
			const form = this.$refs.settingsForm;
			
			// validate with await to ensure getMeta() returns correct result
			const {valid} = await form.validate();
			
			this.$emit('storeUpdated', {
				screen: 'settings',
				valid,
			});				
			/*
			setTimeout(() => { // dirty fix			
				const {valid} = form.getMeta();
				
				this.$emit('storeUpdated', {
					screen: 'settings',
					valid,
				});				
			}, 500);*/
		},
		storeDefaults() {
			// store default answers
			this.securityQuestion1Mounted = this.$store.state.auth.user.securityQuestion1;
			this.securityQuestion2Mounted = this.$store.state.auth.user.securityQuestion2;
			this.recoverySet = (this.securityQuestion1Mounted) ? 1 : 0;
		},
		async savedReset() {
			await this.$store.dispatch('auth/status');
			
			this.storeDefaults();
		}
	},
	watch: {
		saved(bool) {
			if (bool) {
				this.savedReset(); // needed for async/await
			}
		},
		cancelled: {
			// parent triggered cancel
			handler(val) {
				if (!val) { // store reset complete
					const form = this.$refs.settingsForm;
					const errors = form.getErrors();
					
					for(const field in form.getValues()) {
						form.setFieldTouched(field, false);
						
						if (field in errors) {
							form.setFieldError(field, '-');					
						}
					}
				}
			},
			deep: true,
		},
	},
}
</script>

<style lang="scss">
</style>
